import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';

import { PartyDTO } from 'support/types';
import { Button } from 'components/Form/Button';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';

import { PartyDisplay } from './PartyDisplay';
import { PartyModal } from './PartyModal/PartyModal';

enum PartyType {
  Billing = 'billing',
  Delivery = 'delivery',
}

enum ModalState {
  Closed = 'closed',
  Add = 'add',
  Edit = 'edit',
}

type PartySelectorProps = {
  parties: Array<PartyDTO> | undefined;
  selectedPartyId?: string;
  partyType: PartyType;
  onSelectParty: (partyId: string) => void;
  onAddParty: (party: PartyDTO, onSuccess?: () => void) => void;
  onEditParty: (party: PartyDTO, onSuccess?: () => void) => void;
  disabled?: boolean;
  error: string | null;
  clearError: () => void;
  isLoading: boolean;
};

export function Party({
  parties,
  selectedPartyId,
  partyType,
  onSelectParty,
  onAddParty,
  onEditParty,
  disabled,
  error,
  clearError,
  isLoading,
}: PartySelectorProps) {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<ModalState>(ModalState.Closed);

  const selectedParty = useMemo(
    () => parties?.find((party) => party.id === selectedPartyId),
    [parties, selectedPartyId],
  );

  const handlePartyChange = (partyId: string) => {
    if (partyId) {
      onSelectParty(partyId);
    }
  };

  const handleEditClick = () => {
    if (selectedParty) {
      setModalState(ModalState.Edit);
    }
  };

  const handleAddClick = () => {
    setModalState(ModalState.Add);
  };

  const handleModalClose = () => {
    setModalState(ModalState.Closed);
    clearError();
  };

  const handleModalSave = (party: PartyDTO) => {
    if (modalState === ModalState.Add) {
      onAddParty(party, handleModalClose);
    } else if (modalState === ModalState.Edit) {
      onEditParty(party, handleModalClose);
    }
  };

  const dropdownOptions = parties
    ?.filter((party): party is PartyDTO & { id: string } => !!party.id && !!party.street)
    .map((party) => ({
      value: party.id,
      label: party.street,
    }));

  if (!dropdownOptions || dropdownOptions.length === 0) {
    return (
      <>
        <div className="flex w-full flex-col items-start gap-6">
          <Dropdown
            id="party-select"
            wrapperClassName="w-full"
            options={[{ value: '', label: t('orderForms:checkout.noSavedAddresses') }]}
            value=""
            onChange={() => undefined}
            disabled
          />
          <Button
            variant="secondary"
            LeftIcon={PlusIcon}
            onClick={handleAddClick}
            analyticsId="party-selector-add-new-empty"
          >
            {t('orderForms:checkout.addNewAddress')}
          </Button>
        </div>

        <PartyModal
          isOpen={modalState !== ModalState.Closed}
          mode={ModalState.Add}
          partyType={partyType}
          onClose={handleModalClose}
          onSave={handleModalSave}
          errorMessage={error}
          isLoading={isLoading}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col items-start gap-6">
        <Dropdown
          id="party-select"
          wrapperClassName="w-full"
          options={dropdownOptions}
          value={selectedPartyId || ''}
          onChange={handlePartyChange}
          disabled={disabled}
        />

        {selectedParty && <PartyDisplay party={selectedParty} />}
        <div className="flex w-full justify-between">
          {selectedParty && (
            <Button
              LeftIcon={PencilSquareIcon}
              variant="secondary"
              size="small"
              onClick={handleEditClick}
              analyticsId="party-selector-edit"
              disabled={disabled}
            >
              {t('common:edit')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="small"
            LeftIcon={PlusIcon}
            onClick={handleAddClick}
            analyticsId="party-selector-add-new"
            disabled={disabled}
          >
            {t('orderForms:checkout.addNewAddress')}
          </Button>
        </div>
      </div>

      <PartyModal
        isOpen={modalState !== ModalState.Closed}
        mode={modalState === ModalState.Add ? ModalState.Add : ModalState.Edit}
        address={modalState === ModalState.Edit ? selectedParty : undefined}
        partyType={partyType}
        onClose={handleModalClose}
        onSave={handleModalSave}
        errorMessage={error}
        isLoading={isLoading}
      />
    </>
  );
}
